import ReactEcharts from 'echarts-for-react'
import { useEffect, useState } from 'react'

import { ContentCard, Leaderboard } from '@/components/customComponents'

import {
  barChartOptions,
  lineChartOptions,
  pieChartOptions,
} from '@/constants/CHART_CONSTANTS'

import {
  MOCK_COMPLIANCE_DATA_1,
  MOCK_COMPLIANCE_PIE_CHART_DATA_1,
  MOCK_LAST_5_LEAKAGE_SKUS_DATA_1,
  MOCK_TOP_5_LEAKAGE_SKUS_DATA_1,
} from '@/screens/clarityLens/spendLens/CONSTANTS'

interface ComplianceTabProps {
  itemName: string
}

export const ComplianceTab = ({ itemName }: ComplianceTabProps) => {
  const [compliancePieChartData, setCompliancePieChartData] = useState<{
    record: Record<string, number>
    labels: string[]
  }>({
    record: {},
    labels: [],
  })
  const [complianceData, setComplianceData] =
    useState<typeof MOCK_COMPLIANCE_DATA_1>()
  const [top5LeakageSkusData, setTop5LeakageSkusData] =
    useState<typeof MOCK_TOP_5_LEAKAGE_SKUS_DATA_1>()
  const [last5LeakageSkusData, setLast5LeakageSkusData] =
    useState<typeof MOCK_LAST_5_LEAKAGE_SKUS_DATA_1>()

  useEffect(() => {
    if (itemName === 'Oat Milk') {
      setCompliancePieChartData(MOCK_COMPLIANCE_PIE_CHART_DATA_1)
      setComplianceData(MOCK_COMPLIANCE_DATA_1)
      setTop5LeakageSkusData(MOCK_TOP_5_LEAKAGE_SKUS_DATA_1)
      setLast5LeakageSkusData(MOCK_LAST_5_LEAKAGE_SKUS_DATA_1)
    } else if (itemName === 'Almond Croissant') {
      setCompliancePieChartData(MOCK_COMPLIANCE_PIE_CHART_DATA_1)
      setComplianceData(MOCK_COMPLIANCE_DATA_1)
      setTop5LeakageSkusData(MOCK_TOP_5_LEAKAGE_SKUS_DATA_1)
      setLast5LeakageSkusData(MOCK_LAST_5_LEAKAGE_SKUS_DATA_1)
    } else {
      setCompliancePieChartData(MOCK_COMPLIANCE_PIE_CHART_DATA_1)
      setComplianceData(MOCK_COMPLIANCE_DATA_1)
      setTop5LeakageSkusData(MOCK_TOP_5_LEAKAGE_SKUS_DATA_1)
      setLast5LeakageSkusData(MOCK_LAST_5_LEAKAGE_SKUS_DATA_1)
    }
  }, [])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ContentCard
          title="Compliance %"
          loading={!compliancePieChartData}
          isBeta
        >
          {compliancePieChartData && (
            <ReactEcharts
              option={getPieChartData(
                compliancePieChartData.record,
                compliancePieChartData.labels
              )}
            />
          )}
        </ContentCard>
        <ContentCard title="Compliance %" loading={!complianceData} isBeta>
          {complianceData && (
            <ReactEcharts option={getLineChartData(complianceData)} />
          )}
        </ContentCard>
        <ContentCard
          title="Compliance by Stores - Leakage"
          loading={!top5LeakageSkusData}
          isBeta
        >
          {top5LeakageSkusData && (
            <ReactEcharts
              option={getBarChartData([
                { name: 'Vista', value: 37.78 },
                { name: 'Harrington', value: 35.32 },
                { name: 'Silverbrook', value: 35.2 },
                { name: 'Ashburn', value: 34.99 },
                { name: 'Kingbury', value: 32.9 },
              ])}
            />
          )}
        </ContentCard>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Leaderboard
          title="Top 5 Leakage Stores"
          data={top5LeakageSkusData}
          columns={[
            {
              header: 'Store Name',
              accessorKey: 'storeName',
              type: 'text',
            },
            {
              header: 'Leakage Loss',
              accessorKey: 'leakageLoss',
              type: 'money',
            },
            {
              header: 'Supplier Compliance',
              accessorKey: 'supplierCompliancePercentage',
              type: 'percentage',
            },
            {
              header: 'SKU Compliance',
              accessorKey: 'skuCompliancePercentage',
              type: 'percentage',
            },
            {
              header: 'Supplier Mix',
              accessorKey: 'supplierMix',
              type: 'text', // add names in same cell with %s
            },
            {
              header: '% of orders on P-card',
              accessorKey: 'ordersOnPcardPercentage',
              type: 'percentage',
            },
            {
              header: 'Last Compliance Breach Date',
              accessorKey: 'lastComplianceBreachDate',
              type: 'text',
            },
          ]}
          loading={!top5LeakageSkusData}
          enablePagination
          itemsPerPage={5}
          isBeta
        />
        <Leaderboard
          title="Bottom 5 Leakage Stores"
          data={last5LeakageSkusData}
          columns={[
            {
              header: 'Store Name',
              accessorKey: 'storeName',
              type: 'text',
            },
            {
              header: 'Leakage Loss',
              accessorKey: 'leakageLoss',
              type: 'money',
            },
            {
              header: 'Supplier Compliance',
              accessorKey: 'supplierCompliancePercentage',
              type: 'percentage',
            },
            {
              header: 'SKU Compliance',
              accessorKey: 'skuCompliancePercentage',
              type: 'percentage',
            },
            {
              header: 'Supplier Mix',
              accessorKey: 'supplierMix',
              type: 'text', // add names in same cell with %s
            },
            {
              header: '% of orders on P-card',
              accessorKey: 'ordersOnPcardPercentage',
              type: 'percentage',
            },
            {
              header: 'Last Compliance Breach Date',
              accessorKey: 'lastComplianceBreachDate',
              type: 'text',
            },
          ]}
          loading={!last5LeakageSkusData}
          enablePagination
          itemsPerPage={5}
          showBadge={false}
          isBeta
        />
      </div>
    </div>
  )
}

const getPieChartData = (dataObj: Record<string, number>, labels: string[]) => {
  const data = labels.map((label) => dataObj[label] ?? 0)
  return pieChartOptions(data, labels, 'percentage')
}

const getBarChartData = (storeData: { name: string; value: number }[]) => {
  const rawData = [storeData.map((item) => item.value)]
  const seriesNames = ['Sales']
  const xAxisNames = storeData.map((item) => item.name)

  return barChartOptions(rawData, seriesNames, xAxisNames, 'money')
}

const getLineChartData = (data: typeof MOCK_COMPLIANCE_DATA_1) => {
  // Safety check for empty data
  if (!data || Object.keys(data).length === 0) {
    return lineChartOptions([], [], [], 'percentage')
  }

  const periods = Object.keys(data) // These will be our line names/series
  const allMonths = new Set<string>()

  // Collect all unique months (these will be our X-axis data points)
  periods.forEach((period) => {
    if (
      data[period as keyof typeof data] &&
      Array.isArray(data[period as keyof typeof data])
    ) {
      data[period as keyof typeof data].forEach((item) => {
        if (item && item.month) {
          allMonths.add(item.month)
        }
      })
    }
  })

  const monthNames = Array.from(allMonths) // Months should be in their natural order

  // Safety check for no months found
  if (monthNames.length === 0) {
    return lineChartOptions([], [], [], 'percentage')
  }

  // Create raw data for each period (line)
  const rawData = periods.map((period) => {
    return monthNames.map((month) => {
      const monthData = data[period as keyof typeof data]?.find(
        (item: any) => item?.month === month
      )
      return monthData ? monthData.price : 0
    })
  })

  return lineChartOptions(rawData, periods, monthNames, 'percentage')
}
